import { LoggerService } from '@angular-ru/cdk/logger';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, linkedSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { catchError, map, of, scan, switchMap } from 'rxjs';
import { ILink } from 'src/app/api/modules/core/components/abstract/ILink';
import { IImage } from 'src/app/api/modules/core/dynamic/components/IImage';
import { IFaIcon } from 'src/app/api/modules/icons/fa/IFaIcon';
import { DEFAULT_MAT_PROGRESS_SPINNER_OPTIONS } from 'src/app/app-constants';
import { FaIconComponent } from '../../../../icons/components/fa-icon/fa-icon.component';
import { AbstractLinkComponent } from '../../../components/abstract/abstract-link/abstract-link.component';
import { ImageLoaderService } from '../../../services/image-loader.service';

/**
 * Displayed when the image cannot be loaded.
 */
const ERROR_ICON: IFaIcon = {
  name: 'far:triangle-exclamation',
  size: '2x',
};

/**
 * Portrait images are width-limited on anything wider than this, so they
 * don't fill horizontal screens. Matches the upper bound of the legacy
 * flex-layout 'xs' alias (599.98px) this component previously observed.
 */
const DESKTOP_QUERY = '(min-width: 600px)';

/**
 * The async state of the image download.
 */
interface ImageLoadState {
  /** Object URL of the downloaded blob, once complete. */
  imageData?: string;
  /** Download progress 0–100. */
  progress: number;
  isLoaded: boolean;
  hasError: boolean;
  /**
   * True when the blob came synchronously from the session cache — the image
   * renders with no load affordance (no fade, no spinner, no ghost flash),
   * the way a natively-cached `<img>` would.
   */
  instant: boolean;
}

const INITIAL_LOAD_STATE: ImageLoadState = {
  imageData: undefined,
  progress: 0,
  isLoaded: false,
  hasError: false,
  instant: false,
};

/**
 * The Image Component is the primary way in which Images within the application are used.
 *
 * Images are downloaded as blobs (with progress + retry via the ImageLoaderService)
 * and rendered into a pre-sized "ghost" box when dimensions are known. A genuine
 * network load fades in; a session-cache hit (any remount of an already-seen
 * `src` — tab switches, screen re-resolves) renders instantly with no load
 * affordance, like a natively-cached `<img>`. The image fade-in and the ghost
 * placeholder fade-out respect `prefers-reduced-motion`.
 *
 * ### CSS Custom Properties
 *
 * Set these from any ancestor to restyle the component — they inherit through
 * view encapsulation, so no `::ng-deep` or `:host-context` is needed.
 *
 * | Property | Description | Default |
 * | --- | --- | --- |
 * | `--ds-image-spacer` | Bottom margin of the host | design-system spacer |
 */
@Component({
  selector: 'app-image',
  templateUrl: './image.component.html',
  styleUrls: ['./image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinner, FaIconComponent, AbstractLinkComponent, NgTemplateOutlet],
  // Opt-in "cover" mode (IImage.cover): adds a class the SCSS keys off. Purely
  // additive — when `cover` is falsy (every existing image) no class is applied
  // and rendering is unchanged. Same pattern for the ghostless opt-out
  // (`disableGhost`), which is consumer-driven rather than config-driven.
  host: {
    '[class.app-image-cover]': 'config()?.cover',
    '[class.app-image-ghostless]': 'disableGhost()',
  },
})
export class ImageComponent {
  private readonly loggerService = inject(LoggerService);
  private readonly imageLoaderService = inject(ImageLoaderService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  /**
   * The Config as provided to the element.
   *
   * Nothing renders until a truthy config arrives. A config without a valid
   * `src` renders the error state.
   */
  readonly config = input<IImage | null>();

  /**
   * Disable the loading progress spinner manually.
   */
  readonly disableLoadingProgress = input(false);

  /**
   * Suppress the ghost placeholder's dim background while the image loads —
   * for tiny decorative images (inline icons) whose embedding element already
   * reserves the layout, where the pre-paint flash of the surface-dim box
   * reads as a stray white shape. Only the background goes: the ghost box's
   * geometry (the aspect wrapper that positions the `<img>`) is untouched, so
   * layout is identical with or without it.
   */
  readonly disableGhost = input(false);

  /**
   * The view configuration derived purely from the config: validated fields
   * plus the layout values (flex/min/max width, ghost aspect) that the legacy
   * fxFlex directive used to emit.
   */
  private readonly viewConfig = computed(() => {
    const configuration = this.config();
    if (!configuration) {
      return undefined;
    }

    // SRC — undefined means "invalid config", rendered as the error state
    const src = typeof configuration.src === 'string' && configuration.src.length > 0 ? configuration.src : undefined;

    // ALT — decorative images get an explicit empty alt; a meaningful image
    // gets its provided alt. A non-decorative image with no alt is a
    // misconfiguration: fall back to an empty alt so it still passes AXE (an
    // `<img>` with no alt at all is a violation), and warn (see the effect).
    const decorative = typeof configuration.decorative === 'boolean' ? configuration.decorative : undefined;
    const providedAlt = typeof configuration.alt === 'string' && configuration.alt.length > 0 ? configuration.alt : undefined;
    const alt = decorative === true ? '' : (providedAlt ?? '');

    const link: ILink | undefined = configuration.link || undefined;

    const borderRadius =
      typeof configuration.borderRadius === 'string' && configuration.borderRadius.length > 0 ? configuration.borderRadius : undefined;

    const wrapperWidth =
      typeof configuration.wrapperWidth === 'number' && configuration.wrapperWidth > 0 ? `${configuration.wrapperWidth}px` : '100%';

    // IMAGE DIMENSIONS
    const hasDimensions =
      typeof configuration.width === 'number' &&
      configuration.width > 0 &&
      typeof configuration.height === 'number' &&
      configuration.height > 0;
    const width = hasDimensions ? configuration.width : 0;
    const height = hasDimensions ? configuration.height : 0;

    const isFixedSize = configuration.fixedSize === true;

    // LAYOUT
    //
    // These reproduce what `fxFlex` emitted for each flex value (direction row):
    // a pixel basis also locks `max-width` (and `min-width` when inflexible).
    // See https://codepen.io/drimlike/pen/KKVJybZ — fixed size images keep
    // their size, responsive images may shrink. Images with known dimensions
    // get a "ghost" box pre-sized via the padding-top aspect trick.
    let flex: string;
    let minWidth: string | null = null;
    let maxWidth: string | null = null;
    let hasGhost = false;
    let aspect = '0';

    if (hasDimensions) {
      const widthStyle = `${width}px`;
      hasGhost = true;
      aspect = `calc(${height}/${width}*${wrapperWidth})`;
      if (isFixedSize) {
        flex = `0 0 ${widthStyle}`;
        minWidth = widthStyle;
        maxWidth = widthStyle;
      } else {
        flex = `0 1 ${widthStyle}`;
        maxWidth = widthStyle;
      }
    } else {
      flex = isFixedSize ? '0 0 auto' : '0 1 auto';
    }

    // PROGRESS — only images large enough to host the spinner show progress
    const progressSpinnerDiameter = DEFAULT_MAT_PROGRESS_SPINNER_OPTIONS.diameter || 32;
    const minimalValidDimension = progressSpinnerDiameter * 2;
    const sizeSupportsProgress = hasDimensions && width >= minimalValidDimension && height >= minimalValidDimension;

    const isPortrait = hasDimensions && height > width;

    return {
      src,
      alt,
      decorative,
      link,
      borderRadius,
      wrapperWidth,
      flex,
      minWidth,
      maxWidth,
      hasGhost,
      aspect,
      sizeSupportsProgress,
      isPortrait,
    };
  });

  /**
   * The validated `src`, memoized on the string itself.
   *
   * Everything keyed to the *identity of the image* — the download, and the
   * one-shot load affordances below — depends on THIS signal rather than on
   * `viewConfig().src`, and the indirection is load-bearing.
   *
   * A new `config` object carrying the same `src` is not a new image, and
   * nothing here may treat it as one. Identity stability is not something this
   * component can ask of its consumers: an object literal in a template is
   * rebuilt on every change detection cycle, a view model mapped out of a store
   * is rebuilt on every emission, and a repository that clones its state hands
   * over a fresh config on every write. All of those are ordinary, and all of
   * them mean equal-but-not-identical.
   *
   * `viewConfig` is an object computed, so it takes a fresh identity whenever
   * `config` does. A `linkedSignal` re-runs its computation whenever a producer
   * read inside its `source` changes — it does NOT compare the value `source`
   * returned — so sourcing one off `viewConfig()?.src` fires on every one of
   * those. This computed is the barrier: equal strings don't propagate, so a
   * reset downstream means the image genuinely changed.
   */
  private readonly src = computed(() => this.viewConfig()?.src);

  /**
   * Is the viewport desktop-sized? Drives the portrait width limit.
   */
  private readonly isDesktop = toSignal(this.breakpointObserver.observe(DESKTOP_QUERY).pipe(map((state) => state.matches)), {
    initialValue: this.breakpointObserver.isMatched(DESKTOP_QUERY),
  });

  /**
   * The object URL of the currently displayed blob; revoked on replacement
   * and on destroy to free the blob's memory.
   */
  private currentBlobUrl?: string;

  /**
   * The async download state. Changing `src` cancels any in-flight download
   * (switchMap) and starts over; a missing `src` resets to the initial state.
   */
  private readonly loadState = toSignal(
    toObservable(this.src).pipe(
      switchMap((src) => {
        if (!src) {
          return of(INITIAL_LOAD_STATE);
        }
        return this.imageLoaderService.loadImageToBlob(src).pipe(
          scan((state: ImageLoadState, response) => {
            // a blob means the download completed
            if (response.blob) {
              return {
                imageData: this.trackBlobUrl(URL.createObjectURL(response.blob)),
                progress: 100,
                isLoaded: true,
                hasError: false,
                // a synchronous cache hit is already loaded before the first
                // render — skip the fade (and the spinner/ghost never show)
                instant: response.fromCache === true,
              };
            }
            // otherwise carry forward, updating any meaningful progress
            if (typeof response.progress === 'number' && response.progress > 0 && response.progress < 100) {
              return { ...state, progress: response.progress };
            }
            return state;
          }, INITIAL_LOAD_STATE),
          catchError((error: Error) => {
            this.loggerService.error(error.message);
            return of({ ...INITIAL_LOAD_STATE, hasError: true });
          }),
        );
      }),
    ),
    { initialValue: INITIAL_LOAD_STATE },
  );

  /**
   * Whether the fade-in has already completed for the current image. Set when
   * the CSS animation ends; **reset automatically whenever `src` changes** (a
   * genuinely new image should fade) — that's what makes a `linkedSignal` the
   * right tool over a plain signal.
   *
   * This makes the fade a true one-shot: an already-faded image must not
   * animate again for the life of its `<img>` element. Without it, toggling an
   * ancestor `display: none` → visible (a hidden-then-shown hub tab, an
   * accordion panel) restarts the CSS animation on the already-loaded image —
   * the cache prevents the re-download, but not the re-fade. Re-running the
   * fade on an image that is already on screen reads as a flash, so the reset
   * must key off {@link src} and never off the config object around it.
   */
  private readonly hasFaded = linkedSignal<string | undefined, boolean>({
    source: this.src,
    computation: () => false,
  });

  /** Marks the fade complete so a later re-show can't replay it (see {@link hasFaded}). */
  protected onFadeComplete(): void {
    this.hasFaded.set(true);
  }

  /**
   * Whether the `<img>` has actually painted (its `load` event fired), as
   * opposed to merely having its blob downloaded. A cached blob still has to
   * **decode** before any pixels show, and the `<img>` is inserted the moment
   * the blob is ready — so this is the gap between "blob ready" and "pixels on
   * screen". Reset whenever {@link src} changes (a `linkedSignal`, same as
   * {@link hasFaded}).
   *
   * It gates the ghost placeholder's fade-out (see the SCSS): the gray
   * placeholder stays put through the decode rather than fading to the
   * near-white surface before the image can paint — turning a "white flash
   * then pop" into a "gray skeleton then image".
   *
   * Only a new `src` may reset this. `load` fires once per `<img>`, and a
   * re-render leaves the element in place — so a reset that isn't followed by a
   * genuine reload can never be undone, and would strand the loaded image on a
   * visible gray box for the rest of its life.
   */
  private readonly imgPainted = linkedSignal<string | undefined, boolean>({
    source: this.src,
    computation: () => false,
  });

  /** Marks the image painted (its `load` fired) so the ghost can fade. */
  protected onImagePaint(): void {
    this.imgPainted.set(true);
  }

  /**
   * Everything the template renders from.
   */
  protected readonly templateData = computed(() => {
    const view = this.viewConfig();
    if (!view) {
      return undefined;
    }
    const load = this.loadState();
    return {
      ...view,
      ...load,
      // a config without a valid src is an error state
      hasError: load.hasError || !view.src,
      limitPortraitWidthOnDesktop: view.isPortrait && this.isDesktop(),
      showProgress: view.sizeSupportsProgress && !this.disableLoadingProgress(),
      // suppress the fade once it has played, so a display:none→visible re-show
      // can't restart it on an already-loaded image
      faded: this.hasFaded(),
      // gate the ghost fade on actual paint, not blob-ready, so the gray
      // placeholder holds through the decode instead of flashing white
      painted: this.imgPainted(),
      errorIcon: ERROR_ICON,
    };
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      // The image was loaded as a blob so it should be revoked to free memory
      if (this.currentBlobUrl) {
        URL.revokeObjectURL(this.currentBlobUrl);
      }
    });

    // surface misconfiguration in the console (render-neutral): a config that
    // can never load anything, or a meaningful image carrying no alt text
    effect(() => {
      const configuration = this.config();
      if (!configuration) {
        return;
      }
      const hasSrc = typeof configuration.src === 'string' && configuration.src.length > 0;
      if (!hasSrc) {
        this.loggerService.warn('[Image] A configuration was provided without a valid "src" — rendering the error state.');
        return;
      }
      // non-decorative + no alt: the template falls back to an empty alt so the
      // <img> still passes AXE, but a meaningful image should carry real alt
      const hasAlt = typeof configuration.alt === 'string' && configuration.alt.length > 0;
      if (configuration.decorative !== true && !hasAlt) {
        this.loggerService.warn(
          `[Image] A non-decorative image ("${configuration.src}") was provided without "alt" text — falling back to an empty alt. Add meaningful alt text, or set decorative: true.`,
        );
      }
    });
  }

  /**
   * Tracks the active blob object URL, revoking the previous one so that
   * reconfigured images don't leak blob memory.
   */
  private trackBlobUrl(url: string): string {
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
    }
    this.currentBlobUrl = url;
    return url;
  }
}
